/**
 * This page provides the interface to set/remove values into MeasurementsLive page local storage
 * from another page, no matter that page is in the same origin or not.
 * 
 * Usage:
 * 1. Add an `iframe` into that webpage and set the url of this page as `src`
 * <iframe id="localStorage" src="https://measurementslive.ni.com/local-storage/index.html" frameborder="0"></iframe>
 * 2. Use `iframe.contentWindow.postMessage(data)` to send command into the page to set/remove MultisimLive info as an item in the list
 * 
 * Example: `example-local-storage-setter.js`
 */
(function() {
	const multisimLiveListJSONKey = 'multisimLiveListJSON';
	let addedPairingCode = null; // Cache the pairing code added via this page

	// eslint-disable-next-line require-jsdoc
	function safeGetMultisimLiveList() {
		const multisimLiveListJSON = window.localStorage.getItem(multisimLiveListJSONKey);
		try {
			const multisimLiveList = JSON.parse(multisimLiveListJSON);
			if (!(multisimLiveList instanceof Object)) { return {}; }
			return multisimLiveList;
		} catch (error) {
			return {};
		}
	}

	// eslint-disable-next-line require-jsdoc
	function setMultisimLive(multisimLiveInfo) {
		const { pairingCode } = multisimLiveInfo;
		if (!pairingCode || typeof pairingCode !== 'string') { return; }

		const multisimLiveList = safeGetMultisimLiveList();
		if (addedPairingCode && addedPairingCode !== pairingCode) {
			delete multisimLiveList[addedPairingCode];
		}
		multisimLiveList[pairingCode] = multisimLiveInfo;
		window.localStorage.setItem(multisimLiveListJSONKey, JSON.stringify(multisimLiveList));

		addedPairingCode = pairingCode;
	}

	// eslint-disable-next-line require-jsdoc
	function removeMultisimLive(p_pairingCode) {
		const pairingCodeToRemove = p_pairingCode || addedPairingCode;
		
		if (!pairingCodeToRemove) { return; }
		
		const multisimLiveList = safeGetMultisimLiveList();
		if (multisimLiveList[pairingCodeToRemove]) {
			delete multisimLiveList[pairingCodeToRemove];
			window.localStorage.setItem(multisimLiveListJSONKey, JSON.stringify(multisimLiveList));
		}

		if (pairingCodeToRemove === addedPairingCode) {
			addedPairingCode = null;
		}
	}

	// eslint-disable-next-line require-jsdoc
	function handleStorageUpdate({ key, oldValue, newValue }) {
		switch (key) {
		case multisimLiveListJSONKey:
			if (window.parent === window) { return; }
			window.parent.postMessage({
				localStorage: { key, oldValue, newValue },
			}, '*');
			break;
		}
	}

	// eslint-disable-next-line require-jsdoc
	function handleWindowMessage({ data }) {
		const { action, value } = data;
		switch (action) {
		case 'setMultisimLive':
			setMultisimLive(value);
			break;
		case 'removeMultisimLive':
		{
			const oldValue = window.localStorage.getItem(multisimLiveListJSONKey);
			removeMultisimLive(value);

			// Force notify parent window since the the storage change event will not be triggered in the window who makes the change
			handleStorageUpdate({
				key: multisimLiveListJSONKey,
				oldValue,
				newValue: window.localStorage.getItem(multisimLiveListJSONKey),
			});
			break;
		}
		}
	}

	// eslint-disable-next-line require-jsdoc
	function handleWindowUnload() {
		removeMultisimLive();
	}

	window.addEventListener('message', handleWindowMessage);
	window.addEventListener('unload', handleWindowUnload);
	window.addEventListener('storage', handleStorageUpdate);

	// Force storage update to ensure the parent window get the existing storage item
	handleStorageUpdate({
		key: multisimLiveListJSONKey,
		oldValue: null,
		newValue: window.localStorage.getItem(multisimLiveListJSONKey),
	});
})();
